<template>
  <v-card max-width="90vw" class="overflow-y-auto">
    
      <v-card-title class="primary white--text">
        <v-btn class="mr-2" icon @click="closeModal">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        {{ questionType }}
      </v-card-title>

      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="4">
            <v-text-field label="Título" v-model="localQuestion.title"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-autocomplete 
              v-model="localQuestion.difficulty" 
              :items="difficulty"
              item-text="name"
              item-value="id"
              prepend-icon="mdi-numeric-1-circle-outline"
              label="Dificultad"
            ></v-autocomplete>
          </v-col>
          <v-col cols="4">
            <v-text-field label="Puntaje" type="number" v-model="localQuestion.points"></v-text-field>
          </v-col>
        </v-row>
        <div v-if="localQuestion.image" class="image-container">
          <v-img class="contained-image" height="40%" width="40%" :src="localQuestion.image"></v-img>
        </div>

        <!-- Solo mostrar alternativas si NO es pregunta de desarrollo (tipo != 0) -->
        <template v-if="isMultipleChoice">
          <v-row class="scrollable-content" v-for="(alternative, i) in localAnswers" :key="i" justify="start" align="center">
            <v-col cols="1">
              <v-btn
                @click="changeCorrectValue(alternative)"
                :color="alternative.isCorrect ? '#43A047': '#B71C1C'"
                dark
              >
                <v-icon dark fab>
                  {{ alternative.isCorrect ? 'mdi-check' : 'mdi-cancel' }}
                </v-icon>
              </v-btn>
            </v-col>
            <v-col cols="9">
              <v-text-field
                v-model="alternative.description"
                prepend-icon="mdi-format-cancel"
                :label="`Alternativa ${i + 1}`"
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-btn icon color="error" @click="removeAlternative(i)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          
          <!-- Botón para agregar nueva alternativa -->
          <v-row justify="center" class="mt-3">
            <v-btn color="primary" @click="addNewAlternative">
              <v-icon left>mdi-plus</v-icon>
              Agregar alternativa
            </v-btn>
          </v-row>
        </template>

        <v-row class="mt-4">
          <v-col cols="9">
             <v-textarea prepend-icon="mdi-content-copy" v-model="localQuestion.description" auto-grow filled
                  color="deep-purple" label="Descripción" ></v-textarea>
          </v-col>
          <v-col cols="3" class="d-flex justify-center align-center ga-2">
            <v-btn class="ma-2 white--text" color="red" @click="closeModal">
              Cancelar
            </v-btn>
            <v-btn class="ma-2 white--text" color="deep-purple accent-4" @click="editQuestion">
              Guardar
            </v-btn>
          </v-col>
        </v-row>
        
      </v-card-text>

    <v-snackbar :color="color" v-model="snackbar">
      {{ text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> OK </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    questionId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      text: '',
      color: null,
      snackbar: false,
      type: [
        { id: 0, name: "Desarrollo" },
        { id: 1, name: "Selección Múltiple (una respuesta correcta)" },
        { id: 2, name: "Selección Múltiple (varias respuestas correctas)" },
      ],
      difficulty: [
        { id: 0, name: "Fácil" },
        { id: 1, name: "Intermedio" },
        { id: 2, name: "Difícil" },
      ],
      loading: false,
      localQuestion: null,
      localAnswers: null,
      deletedAnswerIds: [] // Para rastrear respuestas eliminadas
    };
  },
  watch: {
    questionId: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          await this.loadQuestion();
        }
      }
    }
  },
  computed: {
    question() {
      return this.$store.getters['teacher/getQuestion'];
    },
    answers() {
      return this.$store.getters['teacher/getAnswers'];
    },
    questionType() {
      switch (this.localQuestion?.type) {
        case 0: return 'Desarrollo';
        case 1: return 'Selección Múltiple (una respuesta correcta)';
        case 2: return 'Selección Múltiple (varias respuestas correctas)';
        default: return '';
      }
    },
    isMultipleChoice() {
      // Las preguntas de tipo 1 y 2 son selección múltiple
      return this.localQuestion?.type === 1 || this.localQuestion?.type === 2;
    }
  },
  methods: {
    createLocalCopies() {
      this.localQuestion = JSON.parse(JSON.stringify(this.question));
      this.localAnswers = JSON.parse(JSON.stringify(this.answers));
      this.deletedAnswerIds = []; // Reiniciar arreglo de eliminados
    },
    async loadQuestion() {
      await this.$store.dispatch('teacher/question', this.questionId);
      this.createLocalCopies();
    },
    closeModal() {
      this.text = '';
      this.color = null;
      this.snackbar = false;
      this.loading = false;
      this.$emit('close');
    },
    addNewAlternative() {
      // Generar un ID temporal negativo para nuevas alternativas
      const tempId = -1 * (this.localAnswers.length + 1);
      
      this.localAnswers.push({
        id: tempId, // ID temporal negativo para identificar nuevas alternativas
        description: '',
        isCorrect: false,
        questionId: this.questionId,
        isNew: true // Marcar como nueva
      });
    },
    removeAlternative(index) {
      // Si tenemos menos de 3 alternativas, no permitir eliminación
      if (this.localAnswers.length <= 2) {
        this.showNotification('orange', 'Debe haber al menos 2 alternativas');
        return;
      }

      const alternative = this.localAnswers[index];
      
      // Si la alternativa ya existe en la BD, guardar su ID para eliminarla
      if (!alternative.isNew && alternative.id > 0) {
        this.deletedAnswerIds.push(alternative.id);
      }
      
      // Eliminar de la lista local
      this.localAnswers.splice(index, 1);
      
      // Si es selección única y se eliminó la correcta, seleccionar la primera como correcta
      if (this.localQuestion.type === 1 && alternative.isCorrect && this.localAnswers.length > 0) {
        this.$set(this.localAnswers, 0, { ...this.localAnswers[0], isCorrect: true });
      }
    },
    changeCorrectValue(alternative) {
      const index = this.localAnswers.findIndex(a => a.id === alternative.id);
      if (index !== -1) {
        // Si es de tipo selección única, deselecciona los demás
        if (this.localQuestion.type === 1) {
          this.localAnswers.forEach((a, i) => {
            this.$set(this.localAnswers, i, { ...a, isCorrect: i === index });
          });
        } else {
          // Si es selección múltiple, solo invierte el estado de la alternativa seleccionada
          this.$set(this.localAnswers, index, {
            ...this.localAnswers[index],
            isCorrect: !this.localAnswers[index].isCorrect
          });
        }
      }
    },
    async editQuestion() {
      try {
        // Validar datos según el tipo de pregunta
        if (this.isMultipleChoice) {
          // Solo para preguntas de selección múltiple: verificar respuesta correcta
          const hasCorrect = this.localAnswers.some(answer => answer.isCorrect);
          if (!hasCorrect) {
            this.showNotification('orange', 'Debe seleccionar al menos una respuesta correcta');
            return;
          }
        }

        // Validar campos obligatorios
        if (!this.localQuestion.title.trim()) {
          this.showNotification('orange', 'El título es obligatorio');
          return;
        }

        // Preparar datos para la API
        const payload = {
          title: this.localQuestion.title,
          difficulty: parseInt(this.localQuestion.difficulty),
          points: parseInt(this.localQuestion.points),
          description: this.localQuestion.description || ''
        };

        // Solo agregar respuestas al payload si es pregunta de selección múltiple
        if (this.isMultipleChoice) {
          payload.answers = this.localAnswers.map(answer => ({
            id: answer.isNew ? null : answer.id, // Enviar null para nuevas alternativas
            description: answer.description,
            isCorrect: answer.isCorrect,
            questionId: this.questionId
          }));
          payload.deletedAnswerIds = this.deletedAnswerIds; // IDs de respuestas a eliminar
        }

        // Actualizar datos de la pregunta
        await axios.put(
          `teacher/question/update/${this.questionId}`,
          payload
        );

        this.showNotification('green', '¡Pregunta actualizada!');
        
        // Recargar la pregunta para actualizar la vista
        await this.loadQuestion();
      } catch (error) {
        console.error('Error detallado:', error.response?.data);
        this.showNotification('red', error.response?.data?.error || 'Error al guardar');
      }
    },
    showNotification(color, text) {
      this.color = color;
      this.text = text;
      this.snackbar = true;
    }
  }
};
</script>